import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { Shell, RequirePermission } from './Shell.jsx';
import { Login } from '../pages/Login.jsx';

/**
 * The admin route table.
 *
 * Only the login screen loads eagerly. Everything else is split, so an editor
 * who only ever opens the blog never downloads the order grid or the charting
 * library.
 *
 * `RequirePermission` mirrors the server's check so an operator gets an
 * explanation rather than a wall of 403s. The server checks again regardless.
 */

const page = (loader, name) =>
  lazy(async () => {
    const module = await loader();
    return { default: module[name] ?? module.default };
  });

const guard = (module, action, element) => (
  <RequirePermission module={module} action={action}>
    {element}
  </RequirePermission>
);

// The dashboard is split despite being the landing screen: Recharts is ~100 kB
// gzipped and putting it in the entry makes every other screen pay for it.
const Dashboard = page(() => import('../pages/Dashboard.jsx'), 'Dashboard');

const Products = page(() => import('../pages/catalogue/Products.jsx'), 'Products');
const ProductEditor = page(() => import('../pages/catalogue/ProductEditor.jsx'), 'ProductEditor');
const Collections = page(() => import('../pages/catalogue/Collections.jsx'), 'Collections');
const CollectionEditor = page(() => import('../pages/catalogue/Collections.jsx'), 'CollectionEditor');
const Categories = page(() => import('../pages/catalogue/Categories.jsx'), 'Categories');
const CategoryEditor = page(() => import('../pages/catalogue/Categories.jsx'), 'CategoryEditor');
const Brands = page(() => import('../pages/catalogue/Brands.jsx'), 'Brands');
const BrandEditor = page(() => import('../pages/catalogue/Brands.jsx'), 'BrandEditor');
const Bottles = page(() => import('../pages/catalogue/Bottles.jsx'), 'Bottles');
const BottleEditor = page(() => import('../pages/catalogue/Bottles.jsx'), 'BottleEditor');
const Combos = page(() => import('../pages/catalogue/Combos.jsx'), 'Combos');
const ComboEditor = page(() => import('../pages/catalogue/Combos.jsx'), 'ComboEditor');
const Attributes = page(() => import('../pages/catalogue/Attributes.jsx'), 'Attributes');
const AttributeEditor = page(() => import('../pages/catalogue/Attributes.jsx'), 'AttributeEditor');
const MediaLibrary = page(() => import('../pages/catalogue/MediaLibrary.jsx'), 'MediaLibrary');

const Orders = page(() => import('../pages/orders/Orders.jsx'), 'Orders');
const OrderDetail = page(() => import('../pages/orders/OrderDetail.jsx'), 'OrderDetail');
const Receivables = page(() => import('../pages/orders/Receivables.jsx'), 'Receivables');
const Customers = page(() => import('../pages/orders/Customers.jsx'), 'Customers');
const Newsletter = page(() => import('../pages/orders/Newsletter.jsx'), 'Newsletter');
const CustomerDetail = page(() => import('../pages/orders/Customers.jsx'), 'CustomerDetail');
const Coupons = page(() => import('../pages/orders/Coupons.jsx'), 'Coupons');
const CouponEditor = page(() => import('../pages/orders/Coupons.jsx'), 'CouponEditor');
const Reviews = page(() => import('../pages/orders/Reviews.jsx'), 'Reviews');

const Blog = page(() => import('../pages/content/Blog.jsx'), 'Blog');
const BlogCategoryEditor = page(() => import('../pages/content/Blog.jsx'), 'BlogCategoryEditor');
const BlogEditor = page(() => import('../pages/content/BlogEditor.jsx'), 'BlogEditor');
const Pages = page(() => import('../pages/content/Pages.jsx'), 'Pages');
const PageEditor = page(() => import('../pages/content/Pages.jsx'), 'PageEditor');
const Enquiries = page(() => import('../pages/content/Enquiries.jsx'), 'Enquiries');

const Settings = page(() => import('../pages/settings/Settings.jsx'), 'Settings');
const Account = page(() => import('../pages/settings/Account.jsx'), 'Account');
const Staff = page(() => import('../pages/settings/Staff.jsx'), 'Staff');
const StaffEditor = page(() => import('../pages/settings/Staff.jsx'), 'StaffEditor');
const ActivityLog = page(() => import('../pages/settings/ActivityLog.jsx'), 'ActivityLog');

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: guard('dashboard', 'read', <Dashboard />) },

      { path: 'products', element: guard('products', 'read', <Products />) },
      { path: 'products/new', element: guard('products', 'create', <ProductEditor />) },
      { path: 'products/:id', element: guard('products', 'read', <ProductEditor />) },
      { path: 'collections', element: guard('categories', 'read', <Collections />) },
      { path: 'collections/new', element: guard('categories', 'create', <CollectionEditor />) },
      { path: 'collections/:id', element: guard('categories', 'read', <CollectionEditor />) },
      { path: 'categories', element: guard('categories', 'read', <Categories />) },
      { path: 'categories/new', element: guard('categories', 'create', <CategoryEditor />) },
      { path: 'categories/:id', element: guard('categories', 'read', <CategoryEditor />) },
      { path: 'brands', element: guard('brands', 'read', <Brands />) },
      { path: 'brands/new', element: guard('brands', 'create', <BrandEditor />) },
      { path: 'brands/:id', element: guard('brands', 'read', <BrandEditor />) },
      { path: 'bottles', element: guard('products', 'read', <Bottles />) },
      { path: 'bottles/new', element: guard('products', 'create', <BottleEditor />) },
      { path: 'bottles/:id', element: guard('products', 'read', <BottleEditor />) },
      { path: 'combos', element: guard('products', 'read', <Combos />) },
      { path: 'combos/new', element: guard('products', 'create', <ComboEditor />) },
      { path: 'combos/:id', element: guard('products', 'read', <ComboEditor />) },
      { path: 'attributes', element: guard('attributes', 'read', <Attributes />) },
      { path: 'attributes/:kind/new', element: guard('attributes', 'create', <AttributeEditor />) },
      { path: 'attributes/:kind/:id', element: guard('attributes', 'read', <AttributeEditor />) },
      { path: 'media', element: guard('media', 'read', <MediaLibrary />) },

      { path: 'orders', element: guard('orders', 'read', <Orders />) },
      /* Before the :id route, or "receivables" is matched as an order id. */
      { path: 'orders/receivables', element: guard('orders', 'read', <Receivables />) },
      { path: 'orders/:id', element: guard('orders', 'read', <OrderDetail />) },
      { path: 'customers', element: guard('customers', 'read', <Customers />) },
      { path: 'newsletter', element: guard('customers', 'read', <Newsletter />) },
      { path: 'customers/:id', element: guard('customers', 'read', <CustomerDetail />) },
      { path: 'coupons', element: guard('coupons', 'read', <Coupons />) },
      { path: 'coupons/new', element: guard('coupons', 'create', <CouponEditor />) },
      { path: 'coupons/:id', element: guard('coupons', 'read', <CouponEditor />) },
      { path: 'reviews', element: guard('reviews', 'read', <Reviews />) },

      { path: 'blog', element: guard('blog', 'read', <Blog />) },
      { path: 'blog/categories/new', element: guard('blog', 'create', <BlogCategoryEditor />) },
      { path: 'blog/categories/:id', element: guard('blog', 'read', <BlogCategoryEditor />) },
      { path: 'blog/new', element: guard('blog', 'create', <BlogEditor />) },
      { path: 'blog/:id', element: guard('blog', 'read', <BlogEditor />) },
      { path: 'pages', element: guard('pages', 'read', <Pages />) },
      { path: 'pages/:id', element: guard('pages', 'read', <PageEditor />) },
      { path: 'enquiries', element: guard('pages', 'read', <Enquiries />) },

      { path: 'settings', element: guard('settings', 'read', <Settings />) },
      { path: 'account', element: <Account /> },
      { path: 'staff', element: guard('staff', 'read', <Staff />) },
      { path: 'staff/new', element: guard('staff', 'create', <StaffEditor />) },
      { path: 'staff/:id', element: guard('staff', 'read', <StaffEditor />) },
      { path: 'activity', element: guard('activity', 'read', <ActivityLog />) },
    ],
  },
]);

export default router;
